// src/lib/useClientSort.ts
//
// ★★★ SORTING IN THE BROWSER — AND THE ONE CONDITION UNDER WHICH IT IS HONEST.
//
// ══ ⚠ READ THIS BEFORE USING THIS HOOK ══
//
// **A client-side sort may only be used where the browser holds the WHOLE list.**
// Not "the whole page" — the whole list. If the route caps, pages or truncates
// what it returns, this hook reorders a window into the data while the header it
// is wired to claims to have reordered the data. That failure is silent, it is
// invisible on a seeded estate small enough to fit one page, and it is the exact
// shape of the defect Wave 16 recorded on the Records enquiry.
//
// `sortable-header-census.test.ts` enforces the condition mechanically: a page
// that imports this hook AND a pager fails the suite. That check is crude on
// purpose — it cannot see a route's own `LIMIT` — so the judgement still has to be
// made here, per surface:
//
//   ✔ `IdentityProvidersPage` — the provider catalogue is four rows and
//     `GET /api/identity-providers` returns all of them, uncapped.
//   ✔ `ReferenceDataPage` — the entries in force for one parameter at one
//     service date, and the indicator reconciliation's fixed verdict set.
//   ✘ Anything with a `?cursor=`, an `?offset=`, or a route-side `LIMIT`. Those
//     sort on the SERVER (`useSort` + `sortParams`, and a whitelist in the
//     repository) — see `RecordsPage`/`ContactsPage` for the shape.
//
// ══ ★★★ NULLS LAST IN BOTH DIRECTIONS ══
//
// The same rule the server half implements in `@prs2/persistence`'s
// `buildOrderBy`, and it has to be implemented twice because the two sorts happen
// in two places — but it must not MEAN two things, or the same column would sort
// differently depending on which surface a reader is looking at.
//
// A `null` in this product means "not applicable", "nobody authored one" or
// "outside your scope". None of those is an extreme value, so none belongs at the
// top of a list somebody sorted to find the extremes. `undefined` and the empty
// string are treated as null for the same reason: on screen they are all the same
// em dash, and a reader who cannot see the difference must not be given three
// different orders for it.
//
// ══ ⚠ STABILITY IS PART OF THE CONTRACT ══
//
// `Array.prototype.sort` is stable in every engine this app supports, and the
// comparator returns 0 for equal keys rather than falling through to some
// arbitrary secondary — so rows that tie keep the order the SERVER sent them in.
// That is the client-side equivalent of the tiebreaker chain every server sort
// here ends in, and without it a re-render could reorder tied rows under the
// reader's cursor.
import { useCallback, useMemo, useState } from 'react'
import type { SortDirection, SortState } from './useSort'

/**
 * How one column's value is read off a row, and how two of them compare.
 *
 * ⚠ `type` is DECLARED rather than sniffed from the first row's value. A column
 * that is null in every row a test happens to seed would be sniffed as neither
 * text nor number, and a numeric column whose first row is null would sort
 * lexically — putting 9 above 10 — which is the kind of defect that survives
 * review because the screen still looks sorted.
 */
export interface ClientSortColumn<Row> {
  /**
   * `text` compares with `localeCompare` under `en-SG`; `number` numerically;
   * `date` and `instant` compare the ISO string, which for ISO-8601 in a single
   * zone is the same order as the instant and avoids parsing every value on every
   * keystroke.
   *
   * ⚠ `date`/`instant` is therefore ONLY correct for canonical ISO values —
   * `2026-08-24`, `2026-08-24T01:02:03Z`. Every date this app renders comes from
   * the server in that form (`formatIsoDate`/`formatInstant` format for DISPLAY
   * and are never fed back in here), and a locally formatted date string would
   * sort by its first character instead. Pass `text` if you ever have one.
   */
  readonly type: 'text' | 'number' | 'date' | 'instant' | 'boolean'
  /**
   * The value to sort on, read from the row. Return `null` for "this row has no
   * value" — the null partition, which sorts last in both directions.
   */
  readonly valueOf: (row: Row) => string | number | boolean | null | undefined
}

export type ClientSortColumns<Row, K extends string> = Readonly<Record<K, ClientSortColumn<Row>>>

export interface UseClientSortResult<Row, K extends string> {
  /** The rows in the current order — the input array itself when unsorted. */
  readonly rows: readonly Row[]
  readonly sort: SortState<K>
  /** Ascending -> descending -> back to the order the server sent. */
  readonly onSort: (column: K) => void
  /** True once the reader has clicked any header — for the live region. See `useSort`. */
  readonly interacted: boolean
}

/** `null` for anything a reader sees as absent — see this file's header on why the empty string counts. */
function normalise(value: string | number | boolean | null | undefined): string | number | boolean | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && value.trim().length === 0) return null
  return value
}

const TEXT = new Intl.Collator('en-SG', { numeric: true, sensitivity: 'base' })

function compareValues(
  a: string | number | boolean | null,
  b: string | number | boolean | null,
  type: ClientSortColumn<unknown>['type'],
): number {
  // ★ The null partition, both directions. Returned BEFORE the direction is
  // applied by the caller — see `useClientSort` — because a null must not flip
  // to the top when the reader clicks a second time.
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1

  if (type === 'number') return Number(a) - Number(b)
  if (type === 'boolean') {
    // false before true ascending. Arbitrary between two booleans, but it has to
    // be SOMETHING fixed, or a re-render reorders the table.
    return Number(Boolean(a)) - Number(Boolean(b))
  }
  // `text`, `date` and `instant`. The collator's `numeric` option is what makes a
  // text column holding "HSP-2" and "HSP-10" order the way a reader expects; it is
  // harmless on an ISO date, where every field is fixed-width.
  return TEXT.compare(String(a), String(b))
}

/**
 * The click cycle, as a pure transition: ascending -> descending -> back to the
 * order the server sent.
 *
 * ⚠ Exported and pure for the same reason `advancePager`/`retreatPager` are —
 * this app has no component-rendering harness for hooks, so the state machine is
 * lifted out and asserted directly, and the hook below is the thin `useState`
 * wrapper around it. See `usePager.ts`'s own header.
 */
export function advanceSort<K extends string>(previous: SortState<K>, column: K): SortState<K> {
  if (previous.column !== column) return { column, dir: 'asc' }
  if (previous.dir === 'asc') return { column, dir: 'desc' }
  // The third click returns to the order the SERVER sent, exactly as `useSort`'s
  // does. A reader who sorted by accident gets back to where they started
  // without reloading.
  return { column: null, dir: 'asc' }
}

/**
 * Order a list the browser holds in full.
 *
 * ⚠ **The direction is applied to the VALUE comparison only, never to the null
 * partition.** That is what makes "nulls last in both directions" true: negating
 * the whole comparator — the obvious one-line implementation — would put every
 * null row at the TOP of a descending sort, which is precisely the PostgreSQL
 * default behaviour this project's standing rule exists to override.
 *
 * ⚠ Returns the INPUT ARRAY unchanged when unsorted, and a COPY otherwise.
 * Sorting in place would mutate the array `useApi` holds, and the third click
 * could then never get back to the server's own order because that order would be
 * gone.
 */
export function sortRows<Row, K extends string>(
  rows: readonly Row[],
  columns: ClientSortColumns<Row, K>,
  sort: SortState<K>,
): readonly Row[] {
  const key = sort.column
  if (key === null) return rows
  const column = columns[key]
  if (column === undefined) return rows
  const direction: SortDirection = sort.dir
  return [...rows].sort((left, right) => {
    const a = normalise(column.valueOf(left))
    const b = normalise(column.valueOf(right))
    if (a === null || b === null) return compareValues(a, b, column.type)
    const ordered = compareValues(a, b, column.type)
    return direction === 'asc' ? ordered : -ordered
  })
}

/** Sort a list the browser holds in full — see this file's header for when that is true. */
export function useClientSort<Row, K extends string>(
  rows: readonly Row[],
  columns: ClientSortColumns<Row, K>,
): UseClientSortResult<Row, K> {
  const [sort, setSort] = useState<SortState<K>>({ column: null, dir: 'asc' })
  const [interacted, setInteracted] = useState(false)

  const onSort = useCallback((column: K) => {
    setInteracted(true)
    setSort((previous) => advanceSort(previous, column))
  }, [])

  const sorted = useMemo(() => sortRows(rows, columns, sort), [rows, columns, sort])

  return { rows: sorted, sort, onSort, interacted }
}
